import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { RawAutosuggest as Autosuggest } from '@els/els-react--autosuggest';

const mapTaxonomySuggestion = (item) => ({
  ...item,
  inputValue: item?.value ?? item?.title ?? '',
});

function TaxonomyLookupModalSearch({ search, getSuggestions, searchByKeyword, suggestInit }) {
  const autosuggestRef = useRef(null);
  const inputRef = useRef(null);
  const previousSearchRef = useRef(search);
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  const loadSuggestions = React.useMemo(
    () =>
      debounce((value) => {
        if (!value?.trim()) {
          setSuggestions([]);
          return;
        }

        Promise.resolve(getSuggestions(value))
          .then((items) => setSuggestions((items || []).map(mapTaxonomySuggestion)))
          .catch(Logger.reject);
      }, 500),
    [getSuggestions]
  );

  useEffect(() => {
    if (previousSearchRef.current === search) return;
    previousSearchRef.current = search;
    const nextSearch = search ?? '';
    setSearchValue(nextSearch);
    autosuggestRef.current?.setTextInputValue(nextSearch);
  }, [search]);

  const handleSearch = (valueOrEvent) => {
    const value =
      typeof valueOrEvent === 'string'
        ? valueOrEvent
        : (valueOrEvent?.inputValue ?? valueOrEvent?.value ?? valueOrEvent?.target?.value ?? searchValue);
    searchByKeyword(value);
  };

  const handleInputClear = () => {
    setSearchValue('');
    autosuggestRef.current?.setTextInputValue('');
    setSuggestions([]);
    searchByKeyword('');
    // Clearing removes the trailing clear button that had focus; keep focus in
    // the search field so keyboard users can continue typing without hunting.
    inputRef.current?.focus();
  };

  return (
    <Autosuggest
      ref={autosuggestRef}
      className="taxonomyLookupModal__treeControls_search e2e-taxonomyLookupModal-tree-search"
      shouldAlwaysChooseSuggestion={false}
      shouldCloseOnESC
      suggestions={suggestions}
      textInputProps={{
        id: 'taxonomyLookupModal__treeControls_search',
        label: 'Enter search term',
        placeholder: 'Enter search term',
        autoFocus: !!suggestInit.search,
        size: 'small',
        inputRef,
        iconLeft: {
          id: 'taxonomyLookupModal__treeControls_search-button',
          sprite: 'Search',
          children: 'Search',
          a11y: { name: 'Taxonomy lookup search' },
        },
        iconLeftCallback: handleSearch,
        iconRight: searchValue
          ? {
              id: 'taxonomyLookupModal__treeControls_clear',
              sprite: 'Close',
              isDecorative: true,
              children: 'Clear Search input',
            }
          : undefined,
        iconRightCallback: searchValue ? handleInputClear : undefined,
        onKeyDown: (event) => event.key === 'Enter' && handleSearch(),
      }}
      onChange={(value) => {
        setSearchValue(value);
        loadSuggestions(value);
      }}
      onSelectSuggestion={(result, value) => {
        const selectedValue = (value || result)?.inputValue ?? (value || result)?.value ?? '';
        setSearchValue(selectedValue);
        autosuggestRef.current?.setTextInputValue(selectedValue);
        handleSearch(selectedValue);
      }}
    />
  );
}

TaxonomyLookupModalSearch.propTypes = {
  search: PropTypes.string,
  getSuggestions: PropTypes.func.isRequired,
  searchByKeyword: PropTypes.func.isRequired,
  suggestInit: PropTypes.object,
};

export default TaxonomyLookupModalSearch;
